import styles from "gui/BrowseDashboardsPage/BrowsePage.module.css";
import { Header } from "shared/Header/Header";
import { PageHead } from "gui/head/head";
import { BrowseSubNav } from "gui/browse-shared/sub-nav";
import { Loading } from "gui/loading/loading";
import List from "./List/TeamsList";
import { Empty } from "gui/empty/empty";
import EmptyBecausePageIsOutOfBounds from "gui/browse-shared/EmptyBecausePageIsOutOfBounds";
import BrowsePagination, {
  usePaginationState,
} from "gui/browse-shared/BrowsePagination";
import Sidebar, {
  useFreetextFilterState,
} from "./Sidebar/DiscoverTeamsSidebar";

import { useMemo } from "react";
import useListDiscoverTeams from "./api/useListDiscoverTeams";
import { transformData } from "./api/listDiscoverTeams";

// State
interface Filter {
  freetext: string;
}

export default function DiscoverTeamsPage() {
  // Filter
  const { freetextFilterDebounced } = useFreetextFilterState();

  const filter: Filter = useMemo(
    () => ({
      freetext: freetextFilterDebounced,
    }),
    [freetextFilterDebounced]
  );

  // Pagination
  const [page] = usePaginationState();

  // Parameters to Hasura query
  const apiParams = useMemo(() => ({ filter, page, pageSize: 20 }), [
    filter,
    page,
  ]);

  const teamsResult = useListDiscoverTeams(apiParams);

  return (
    <>
      <PageHead title="Teams" />
      <div className={styles.page}>
        <Header />
        <main>
          <BrowseSubNav className={styles.subNav} />

          <section className={styles.results}>
            <Results teamsResult={teamsResult} pageSize={apiParams.pageSize} />
          </section>

          <Sidebar />
        </main>
      </div>
    </>
  );
}

function Results(props: {
  teamsResult: ReturnType<typeof useListDiscoverTeams>;
  pageSize: number;
}) {
  const { teamsResult, pageSize } = props;
  const [page] = usePaginationState();

  if (teamsResult.error) {
    return <section>Error</section>;
  }
  if (teamsResult.data) {
    const data = transformData(teamsResult.data);

    if (data.total_count === 0) {
      return (
        <Empty icon="circle-fill" title="No matching teams">
          <p>Try another search to find teams.</p>
        </Empty>
      );
    }

    // Check if page from url is out of bounds.
    // Consider there to always be at least one page, even if it's empty.
    const lastPage = Math.max(1, Math.ceil(data.total_count / pageSize));

    if (page > lastPage) {
      return <EmptyBecausePageIsOutOfBounds lastPage={lastPage} />;
    }

    return (
      <>
        <List teams={data.teams} />

        <BrowsePagination pageSize={pageSize} totalCount={data.total_count} />
      </>
    );
  }
  if (teamsResult.loading) {
    return <Loading />;
  }
  // "idle": Not even started initial fetch
  return <Loading />;
}
